// Window Order Email Template
// This template is specifically for window orders

// Constants for template variables
export const WINDOW_TEMPLATE_VARIABLES = [
  { name: 'orderNumber', description: 'The unique order number' },
  { name: 'orderDate', description: 'The date the order was created' },
  { name: 'customerReference', description: 'Customer-provided reference number' },
  { name: 'subtotal', description: 'Order subtotal before VAT' },
  { name: 'vat', description: 'VAT amount' },
  { name: 'total', description: 'Total order amount' },
  { name: 'totalWindows', description: 'Total number of window/door units in the order' },
  { name: 'totalQuantity', description: 'Alias of totalWindows: total number of window/door units in the order' },
  { name: 'totalQty', description: 'Alias of totalWindows: total number of window/door units in the order' },
  { name: 'totalUnits', description: 'Alias of totalWindows: total number of window/door units in the order' },
  { name: 'customerName', description: 'Customer name' },
  { name: 'customerEmail', description: 'Customer email address' },
  { name: 'customerPhone', description: 'Customer phone number' },
  { name: 'customerCompany', description: 'Customer company name (optional)' },
  { name: 'customerAddress', description: 'Customer address (optional)' },
  { name: 'customerNotes', description: 'Additional notes from customer (optional)' }
];

export const WINDOW_ITEM_VARIABLES = [
  { name: 'reference', description: 'Window reference/identifier' },
  { name: 'windowType', description: 'Window type label (e.g. Casement, Tilt & Turn)' },
  { name: 'styleName', description: 'Window style name' },
  { name: 'width', description: 'Window width in mm' },
  { name: 'height', description: 'Window height in mm' },
  { name: 'quantity', description: 'Quantity for this specific fenestration item' },
  { name: 'description', description: 'Window description' },
  { name: 'glassType', description: 'Type of glass used' },
  { name: 'glassSpecification', description: 'Glass specification: Annealed / Toughened / Laminated / Toughened & Laminated / Heat Soak (supplier only)' },
  { name: 'frameColor', description: 'Color of the window frame' },
  { name: 'windowColour', description: 'Alias of frameColor (legacy templates)' },
  { name: 'frameColorExternal', description: 'External frame colour (Visual Designer lines)' },
  { name: 'frameColorInternal', description: 'Internal frame colour (Visual Designer lines)' },
  { name: 'windowMaterial', description: 'Window material (e.g. uPVC, Aluminium, Timber)' },
  { name: 'material', description: 'Alias of windowMaterial' },
  { name: 'profileDepth', description: 'Profile depth (if available)' },
  { name: 'profileType', description: 'Profile type (e.g. Sculptured/Chamfered)' },
  { name: 'beadingType', description: 'Beading type (if available)' },
  { name: 'hingeType', description: 'Window hinge type (Standard / Egress / EZ Clean / Fire-Rated) (windows only)' },
  { name: 'astragalBarSide', description: 'Astragal bar side (Inside / Outside / Both)' },
  { name: 'georgianBars', description: 'Georgian bars grid and width (e.g. 3×2 18mm); empty when none' },
  { name: 'georgianBarsRowStyle', description: 'display:none; when there are no Georgian bars (hide the row)' },
  { name: 'handleStyle', description: 'Style of the window handle' },
  { name: 'handleColor', description: 'Color of the window handle' },
  { name: 'hingePlacement', description: 'Door hinge placement (Left/Right) (doors only)' },
  { name: 'openDirection', description: 'Door open direction (In/Out) (doors only)' },
  { name: 'masterLeaf', description: 'Master leaf viewed from outside (French Doors only)' },
  { name: 'panelCount', description: 'Number of panels (Bi-Folds and Sliders only)' },
  { name: 'stackDirection', description: 'Stack direction viewed from outside (Bi-Folds only)' },
  { name: 'trafficDoor', description: 'Traffic door side viewed from outside (Bi-Folds only)' },
  { name: 'slidingPanels', description: 'Which panels slide, e.g. A2 slides left (Sliders only)' },
  { name: 'threshold', description: 'Threshold: Standard / Low Aluminium / Low uPVC / Mobility MDS25 (all doors, including Door & Window)' },
  { name: 'trickleVents', description: 'Trickle vents combined text (e.g. Yes (In Sash))' },
  { name: 'trickleVentsYesNo', description: 'Trickle vents yes/no only (Yes or No)' },
  { name: 'trickleVentsPosition', description: 'Trickle vents position (In Sash / In Head)' },
  { name: 'cill', description: 'Cill combined text (e.g. Included Regular 150mm)' },
  { name: 'cillIncluded', description: 'Cill included (Yes/No)' },
  { name: 'cillType', description: 'Cill type (Regular/Stub)' },
  { name: 'cillHeight', description: 'Cill height (e.g. 150mm)' },
  { name: 'notes', description: 'Additional notes about this window' }
];

// Template preview function
export const previewWindowTemplate = (template: string): string => {
  // Helper: recursively process Mustache-style blocks
  function renderBlocks(str: string, data: any): string {
    // # blocks
    str = str.replace(/{{#(\w+)}}([\s\S]*?){{\/(\1)}}/g, (match, key, content) => {
      if (data[key]) {
        return renderBlocks(content, data);
      }
      return '';
    });
    // ^ (inverse) blocks
    str = str.replace(/{{\^(\w+)}}([\s\S]*?){{\/(\1)}}/g, (match, key, content) => {
      if (!data[key]) {
        return renderBlocks(content, data);
      }
      return '';
    });
    return str;
  }

  // Sample data for preview
  const sampleData = {
    orderNumber: 'WO-12345',
    orderDate: new Date().toLocaleDateString('en-GB'),
    customerReference: 'REF-789',
    subtotal: '1200.00',
    vat: '240.00',
    total: '1440.00',
    totalWindows: '3',
    totalQuantity: '3',
    totalQty: '3',
    totalUnits: '3',
    customerName: 'John Smith',
    customerEmail: 'john@example.com',
    customerPhone: '07700 900000',
    customerCompany: 'ABC Installations Ltd',
    customerAddress: '123 Main Street, London',
    customerNotes: 'Please deliver before the holidays',
    windows: [
      {
        width: '1200',
        height: '1500',
        quantity: '2',
        description: 'White PVC Double Glazed Window',
        glassType: 'Clear 4-16-4',
        frameColor: 'White',
        handleStyle: 'Standard',
        handleColor: 'Chrome',
        hingeType: 'Standard (default)',
        astragalBarSide: 'Both',
        georgianBars: '3×2 18mm',
        notes: 'Opening inwards'
      },
      {
        width: '900',
        height: '1200',
        quantity: '1',
        description: 'White PVC Double Glazed Window',
        glassType: 'Clear 4-16-4',
        frameColor: 'White',
        handleStyle: 'Standard',
        handleColor: 'Chrome',
        hingeType: 'Egress',
        astragalBarSide: 'Outside',
        notes: 'Fixed window'
      }
    ]
  };

  // Replace variables in the template
  let result = template;
  Object.entries(sampleData).forEach(([key, value]) => {
    if (key === 'windows') {
      // Handle windows array separately
      if (result.includes('{{#windows}}')) {
        const windowTemplate = result.match(/{{#windows}}([\s\S]*?){{\/windows}}/)?.[1] || '';
        const renderedWindows = (value as any[]).map(item => {
          let windowHtml = windowTemplate;
          Object.entries(item).forEach(([itemKey, itemValue]) => {
            windowHtml = windowHtml.replace(new RegExp(`{{${itemKey}}}`, 'g'), String(itemValue));
          });
          // Also render per-window conditional blocks (e.g. {{#hingePlacement}}...{{/hingePlacement}})
          windowHtml = renderBlocks(windowHtml, item);
          return windowHtml;
        }).join('\n');
        result = result.replace(/{{#windows}}[\s\S]*?{{\/windows}}/, renderedWindows);
      }
    } else {
      // Handle other variables
      result = result.replace(new RegExp(`{{${key}}}`, 'g'), String(value));
    }
  });

  // Process any remaining blocks
  result = renderBlocks(result, sampleData);

  return result;
}; 